import { BRAND_TOOL, VOICE_CARD } from '@k2/agents/chat/brand'
import type { Component } from 'vue'
import type { ChatReferenceCard } from './attachment'
import { attachmentsOf, type ChatPicture } from './chip'
import type { AgentTurn, CardPart, ShellTurn, ToolPart } from './turn'

export type ChatSuggestion = { readonly label: string; readonly icon?: Component; readonly text: string }

export type ChatCardWords = { readonly title: string; readonly action: string; readonly hint?: string }

// An own card gets the part, `busy`, `folded`, and `shown` when it has a panel; it emits `answer`, and `open` to show its panel.
// A panel gets the part and `busy`, and emits `answer`, `ask` with a chip for the composer, and `close`.
// A card that `follows` keeps its panel open when approved there, and the panel shows the next card once it arrives.
export type ChatOwnCard = { readonly component: string; readonly panel?: string; readonly follows?: boolean }

export type ChatToolWords = { readonly label: string; readonly detail?: string }

export type ChatView = {
  readonly cards?: Readonly<Record<string, ChatCardWords | ChatOwnCard>>
  readonly toolLabel?: (part: ToolPart) => ChatToolWords | null
  readonly tools?: Readonly<Record<string, { readonly component: string; readonly gather?: boolean }>>
  readonly nextSteps?: (turn: AgentTurn) => readonly ChatSuggestion[]
  readonly referenceOf?: (label: string) => ChatReferenceCard | null
  readonly carries?: (part: CardPart) => readonly ChatPicture[]
}

// Cards every agent can show, drawn the same on every page.
const SHELL_CARDS: Readonly<Record<string, ChatOwnCard>> = {
  [VOICE_CARD]: { component: 'chat-voice-card', panel: 'chat-voice-drawer' }
}

// Tool rows every agent can show: the voice card as saved, with Open.
const SHELL_TOOLS: NonNullable<ChatView['tools']> = { [BRAND_TOOL.show]: { component: 'chat-voice-shown' } }

export const toolsOf = (view: ChatView): NonNullable<ChatView['tools']> => ({ ...SHELL_TOOLS, ...view.tools })

export const cardOf = (view: ChatView, kind: string) => SHELL_CARDS[kind] ?? view.cards?.[kind]

export const ownCardOf = (view: ChatView, kind: string) => {
  const card = cardOf(view, kind)
  return card && 'component' in card ? card : null
}

// FR-E2a: a picture the newest card used that no message attached goes back to the composer, so the user sees it and can remove it.
export const carriedIn = (turns: readonly ShellTurn[], view: ChatView) => {
  const last = turns.at(-1)
  if (last?.role !== 'agent' || !view.carries) return null
  const card = last.parts.findLast((part): part is CardPart => part.kind === 'card' && Boolean(view.carries?.(part).length))
  if (!card) return null
  const attached = new Set(
    turns.flatMap(turn => (turn.role === 'user' ? attachmentsOf(turn.text).attachments.map(picture => picture.id) : []))
  )
  const pictures = view.carries(card).filter(picture => !attached.has(picture.id))
  return pictures.length ? { cardId: card.id, pictures } : null
}
